import React, { useState } from 'react'
import { Box, Typography, Collapse } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

export interface ExpandableCardField<T> {
  label: string
  getValue: (item: T) => React.ReactNode
}

interface ExpandableCardListProps<T> {
  items: T[]
  getKey: (item: T) => string | number
  renderHeader: (item: T) => React.ReactNode
  fields: ExpandableCardField<T>[]
  onSelect: (item: T) => void
}

export const ExpandableCardList = <T,>({
  items,
  getKey,
  renderHeader,
  fields,
  onSelect
}: ExpandableCardListProps<T>) => {
  const [expandedKey, setExpandedKey] = useState<string | number | false>(false)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {items.map((item) => {
        const key = getKey(item)
        const isOpen = expandedKey === key
        return (
          <Box
            key={key}
            sx={{
              display: 'flex',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              overflow: 'hidden',
              backgroundColor: isOpen ? '#fff' : '#f5f5f5'
            }}
          >
            <Box
              sx={{ flex: 1, cursor: 'pointer' }}
              onClick={() => setExpandedKey(isOpen ? false : key)}
            >
              <Box sx={{ px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {renderHeader(item)}
              </Box>
              <Collapse in={isOpen}>
                <Box sx={{ px: 2, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  {fields.map((field) => {
                    const value = field.getValue(item)
                    const isNode = typeof value !== 'string' && typeof value !== 'number'
                    return isNode ? (
                      <Box key={field.label}>
                        <Typography variant='body2' component='span'><b>{field.label}</b></Typography>
                        {value}
                      </Box>
                    ) : (
                      <Typography key={field.label} variant='body2'>
                        <b>{field.label}:</b> {value}
                      </Typography>
                    )
                  })}
                </Box>
              </Collapse>
            </Box>
            {isOpen && (
              <Box
                onClick={() => onSelect(item)}
                sx={{
                  width: 52,
                  backgroundColor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                <ChevronRightIcon sx={{ color: '#fff', fontSize: 28 }} />
              </Box>
            )}
          </Box>
        )
      })}
    </Box>
  )
}
